"use client";

import React, {
  Children,
  KeyboardEvent,
  MouseEvent,
  SyntheticEvent,
  useMemo,
  useState,
} from "react";

import IconButton from "@mui/material/IconButton";
import { Theme } from "@mui/material/styles";
import { TextFieldProps } from "@mui/material/TextField/TextField.d";
import { merge } from "lodash";

import { usePaperPosition } from "~/hooks/use-paper-position/index";
import { Down24 } from "~/theme/icons/components/down";
import { Up24 } from "~/theme/icons/components/up";

import { DROPDOWN_CONTROL_CLASSES } from "./constants";
import { DropdownControlContainer } from "./styled";

export type DropdownControlProps = Omit<
  TextFieldProps & { anchorVerticalOrigin?: number },
  "select" | "variant"
>;

export const DropdownControl: React.FC<DropdownControlProps> = ({ ...props }) => {
  const childrenCount = useMemo(
    () => Children.count(props.children),
    [props.children],
  );
  const {
    id,
    className,
    SelectProps,
    onBlur,
    anchorVerticalOrigin = 48,
    ...restProps
  } = props;

  const { rect, setRect, isTopVariant } = usePaperPosition(childrenCount);

  const [open, setOpen] = useState(false);
  const menuId = `menu-${props.id}`;
  const isPlaceholderVisible = props.value === "";
  const baseSelectProps = {
    open: open,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    inputProps: {
      id: props?.id,
    },
    SelectDisplayProps: {
      "aria-expanded": `${open}`,
      "data-cs-override-id": "",
      ...(!open && {
        "aria-controls": undefined,
      }),
    },
    MenuProps: {
      id: menuId,
      anchorOrigin: {
        vertical: isTopVariant ? -4 : anchorVerticalOrigin,
        horizontal: "left",
      },
      transformOrigin: {
        vertical: isTopVariant ? "bottom" : "top",
        horizontal: "left",
      },
      sx: (theme: Theme) => ({ zIndex: theme.zIndex.appBar }),
      PopoverClasses: {
        paper: DROPDOWN_CONTROL_CLASSES.paper,
      },
      slotProps: {
        root: {
          slotProps: {
            backdrop: {
              sx: {
                background: "transparent",
              },
            },
          },
        },
      },
    },
    // eslint-disable-next-line react/no-multi-comp
    IconComponent: (props: { open: boolean }) => {
      const { open } = props;
      return (
        <IconButton {...props} aria-hidden="true" tabIndex={-1} size="large">
          {open ? <Up24 /> : <Down24 />}
        </IconButton>
      );
    },
  };
  const mergedSelectProps = merge(baseSelectProps, SelectProps);

  return (
    <DropdownControlContainer
      {...restProps}
      rect={rect}
      isTopVariant={isTopVariant}
      select
      isPlaceholderVisible={isPlaceholderVisible}
      onBlur={(event) => {
        setOpen(false);
        if (onBlur) {
          onBlur(event);
        }
      }}
      SelectProps={mergedSelectProps}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.code === "Enter" || event.code === "Space") {
          setRect(event.currentTarget.getBoundingClientRect());
          setOpen(true);
        }
        if (props.onKeyDown) {
          props.onKeyDown(event);
        }
      }}
      onMouseDown={(event: MouseEvent<HTMLDivElement>) => {
        setRect(event.currentTarget.getBoundingClientRect());
        if (props.onMouseDown) {
          props.onMouseDown(event);
        }
      }}
      onTouchMoveCapture={(event: SyntheticEvent) => {
        if (((event.target as Element).parentNode as Element)?.id === menuId) {
          setOpen(false);
        }
      }}
    />
  );
};
